import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { SiteVideoData, SiteImageData } from "../../../../interfaces";
import { MediaContentTypes } from "../../../../../constants/contentTypes";
import { VerticalVideoPlayer } from "../../components/VerticalVideoPlayer";
import { VerticalShareArea } from "../../components/VerticalShareArea";
import { SpeedMenuButton } from "../../components/SpeedMenuButton";
import { DesignItemProps } from "../../interfaces";
import { getImageUrl } from "../../../../../config/apiUrls";
require("./m_ynet_site_flashesMediaItem.less");

export const FlashesMediaItem = (props: DesignItemProps) => {
    const { videoItem, index, domain, isMobile, languageCode, showPrerollAds, isTitleVisible, isSubTitleVisible, isGalleryView, shouldDisplayArrow,
        currentPlayingVideo, containerStyle, dynamicHeight, dynamicWidth, onNextSlide, onVideoOverlayClose } = props;
    const { itemId, media, roofTitle, title, text, publishedLink } = videoItem;

    // Media type detection
    const isVideo = media && (media as any).mediaType === MediaContentTypes.MEDIA_VIDEO;
    const isImage = media && (media as any).mediaType === MediaContentTypes.MEDIA_IMAGE;
    const imageAlt = isImage ? ((media as SiteImageData).alt || title || "") : "";
    const isDefaultImage = isImage ? (media as any).isDefaultImage : false;
    const videoPageId = isVideo ? (media as SiteVideoData).videoPageId : "";
    const mediaCredit = (isVideo || isImage) && media.credit ? media.credit : undefined;

    const [isSubtitleOpen, setIsSubtitleOpen] = useState(isDefaultImage);
    const [isClamped, setIsClamped] = useState(false);
    const [isPortrait, setIsPortrait] = useState(true);
    const [playerApiRef, setPlayerApiRef] = useState(null) as any;
    const subtitleRef = useRef(null) as any;

    useLayoutEffect(() => {
        const el = subtitleRef.current;
        if (el) {
            const wasOpen = !el.classList.contains("close");
            if (wasOpen) el.classList.add("close");
            setIsClamped(el.scrollHeight > el.clientHeight);
            if (wasOpen) el.classList.remove("close");
        }
    }, [text, dynamicWidth]);

    // Aspect ratio detection — portrait if width/height ratio <= threshold (close to 9:16)
    const PORTRAIT_RATIO_THRESHOLD = 0.75;
    const mediaAspectRatio = (media as any).aspectRatio ? (media as any).aspectRatio : undefined;

    useEffect(() => {
        if (mediaAspectRatio) {
            const [w, h] = mediaAspectRatio.split(":").map(Number);
            setIsPortrait(w / h <= PORTRAIT_RATIO_THRESHOLD);
        }
    }, [mediaAspectRatio]);

    const getPosterUrl = () => {
        if (isImage) {
            const image = getImageUrl((media as SiteImageData), { w: dynamicWidth || 640, h: dynamicHeight || 900 });
            return image
        } else if (isVideo) {
            return (media as SiteVideoData).poster;
        }
        return "";
    }

    // Design-specific fields from the item
    const date = (videoItem as any).date;
    const author = (videoItem as any).author || videoItem.author || "";

    const lang = languageCode || "";
    const readMoreLabel = lang.includes("en") ? "Read more..." : lang.includes("ru") ? "Читать далее..." : "קרא עוד...";
    const nextVideoLabel = lang.includes("en") ? "scroll for the next flash" : lang.includes("ru") ? "прокрутите вниз для следующего кадра" : "ניתן לדפדף למבזק הבא";
    const copyLinkLabel = lang.includes("en") ? "copy link" : lang.includes("ru") ? "Скопировать ссылку" : "העתק קישור";
    const linkCopiedLabel = lang.includes("en") ? "Link copied to clipboard" : lang.includes("ru") ? "Ссылка скопирована" : "הקישור הועתק ללוח";

    const subtitleClass = `subtitle ${isSubtitleOpen ? "" : "close"}`;
    const itemType = isVideo ? "video-item" : isImage ? "image-item" : "";
    const aspectClass = isPortrait ? "portrait-media" : "landscape-media";
    const itemClass = `flashes-media-item ${itemType} ${isMobile ? isSubtitleOpen ? "openMode" : "standardMode" : ""}`;
    const shareItemType = isImage ? "vertical_flash_image" : isVideo ? "vertical_flash_video" : "vertical_flash_item"

    return (
        <div className={itemClass} id={`VerticalVideoItem_flashesMediaItem_${itemId}`} style={containerStyle}>
            <div className={`media-wrapper ${aspectClass}`}>
                {isVideo &&
                    <VerticalVideoPlayer
                        index={index}
                        isMobile={isMobile}
                        onNextSlide={onNextSlide}
                        dynamicWidth={dynamicWidth}
                        dynamicHeight={dynamicHeight}
                        isGalleryView={isGalleryView}
                        media={media as SiteVideoData}
                        showPrerollAds={showPrerollAds}
                        prerollIndex={videoItem.prerollIndex}
                        itemId={`flashesMediaItem_${itemId}`}
                        hidePrerollAds={videoItem.hidePrerollAds}
                        currentPlayingVideo={currentPlayingVideo}
                        onPlayerApiReady={(ref) => setPlayerApiRef(ref)}
                    />
                }

                {isImage && <img className="flashes-image" src={getPosterUrl()} alt={imageAlt} />}
            </div>

            <div className={`content-div ${isSubtitleOpen ? "subtitleOpen" : ""}`}>
                <div className="item-info">
                    <div className="text-content">
                        {date && <div className="date">{date}</div>}

                        {roofTitle && <div className="roof-title">{roofTitle}</div>}

                        {isTitleVisible && <div className="title">{title}</div>}

                        {isSubTitleVisible && text &&
                            <div className={subtitleClass} ref={subtitleRef} onClick={isClamped ? () => setIsSubtitleOpen(!isSubtitleOpen) : undefined}>
                                {text}
                                {isClamped && !isSubtitleOpen && <span className="read-more">{readMoreLabel}</span>}
                            </div>
                        }

                        {author && <div className="author">({author}{mediaCredit ? `, ${mediaCredit}` : ""})</div>}

                        {shouldDisplayArrow &&
                            <div className="arrow-next">
                                <video src={"https://www.ynet.co.il/images/picsrv/two-white-arrows-gif.webm"} className="arrow-gif" autoPlay loop muted playsInline ></video>

                                <div className="arrow-text">{nextVideoLabel}</div>
                            </div>
                        }
                    </div>

                    <div className="btns-content">
                        {isVideo && <div className="mute-btn" id="volume-target"></div>}

                        {isVideo && playerApiRef &&
                            <SpeedMenuButton playerApiRef={playerApiRef} />
                        }

                        <VerticalShareArea
                            title={title}
                            domain={domain}
                            articleId={itemId}
                            isMobile={isMobile}
                            link={publishedLink}
                            shareItemType="newsFlash"
                            videoPageId={videoPageId}
                            copyLinkLabel={copyLinkLabel}
                            linkCopiedLabel={linkCopiedLabel}
                            analyticShareLabel={shareItemType}
                        />
                    </div>
                </div>

                {isVideo &&
                    <div className="player-details">
                        <div className="timeline" id="timeline-target"></div>
                    </div>
                }
            </div>
        </div>
    );
};
